<template>
  <div class="scanner-container pa-4">
    <!-- Warnings -->
    <v-alert v-if="!materialHandleSet" type="warning" class="mb-4">
      Materials and handles need to be set before using the scanner.
    </v-alert>
    <v-alert v-if="hasFrontsItems" type="warning" class="mb-4">
      There shouldn't be any fronts items to start the scanner. Please remove existing fronts first.
    </v-alert>

    <!-- Drop Zone -->
    <v-card
      v-if="cabinetGroups.length === 0 && !parsing"
      :class="[
        'drop-zone',
        { 'drop-zone--active': !dropDisabled && dragging },
        { 'drop-zone--disabled': dropDisabled }
      ]"
      border
      @dragover.prevent="dropDisabled ? null : (dragging = true)"
      @dragenter.prevent="dropDisabled ? null : (dragging = true)"
      @dragleave.prevent="dragging = false"
      @drop.prevent="onFileDrop"
    >
      <div class="drop-zone__content text-center">
        <v-icon :size="64" :color="dropDisabled ? 'grey-lighten-2' : dragging ? 'accent' : 'grey-lighten-1'"
          >mdi-file-pdf-box</v-icon
        >
        <p class="text-h6 mt-4" :class="dropDisabled ? 'text-grey-lighten-1' : 'text-grey-darken-1'">
          Drag &amp; drop IKEA planner PDF here
        </p>
        <p class="text-body-2 text-grey">or</p>
        <v-btn color="primary" variant="outlined" :disabled="dropDisabled" @click="$refs.fileInput.click()">
          Browse Files
        </v-btn>
        <input ref="fileInput" type="file" accept=".pdf" style="display: none" @change="onFileSelect" />
      </div>
    </v-card>

    <!-- Loading -->
    <div v-if="parsing" class="text-center py-8">
      <v-progress-linear indeterminate color="accent" class="mb-4" />
      <p class="text-body-1 text-grey">Parsing {{ fileName }}...</p>
    </div>

    <!-- Converting -->
    <div v-if="converting" class="text-center py-8">
      <v-progress-linear indeterminate color="accent" class="mb-4" />
      <p class="text-body-1 text-grey">Converting items to fronts...</p>
    </div>

    <!-- Error -->
    <v-alert v-if="error" type="error" closable class="mb-4" @update:model-value="error = null">
      {{ error }}
    </v-alert>

    <!-- Results -->
    <div v-if="cabinetGroups.length > 0 && !converting">
      <div class="d-flex align-center mb-4">
        <h3 class="text-h6">{{ fileName }}</h3>
        <v-spacer />
        <v-btn size="small" variant="text" color="grey" class="clear-btn" @click="showClearDialog = true">
          <v-icon size="small" start>mdi-close</v-icon>
          Clear
        </v-btn>
      </div>

      <v-card v-for="group in cabinetGroups" :key="group.itemNum" border class="mb-4">
        <v-card-title class="text-subtitle-1 font-weight-bold text-primary py-2 d-flex align-center">
          <span>{{ group.itemNum }}. {{ group.code }} - {{ group.description }}</span>
          <v-spacer />
          <span class="text-body-2 text-grey mr-2">Item num:</span>
          <v-text-field
            v-model="group.customItemNum"
            density="compact"
            single-line
            hide-details
            class="mt-0 pt-0"
            style="max-width: 60px; flex: none"
          />
        </v-card-title>
        <v-divider />

        <v-table density="compact">
          <template v-slot:default>
            <thead>
              <tr>
                <th style="width: 40px"></th>
                <th>Title</th>
                <th>Type</th>
                <th>Position</th>
                <th>Width</th>
                <th>Height</th>
                <th>Qty</th>
                <th v-if="groupHasDoors(group)">Hinge</th>
                <th v-if="groupHasDoors(group)">Hung</th>
                <th v-if="groupHasDoors(group)">Special</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in group.items" :key="item.id">
                <td>
                  <v-checkbox v-model="item.selected" density="compact" hide-details class="mt-0 pt-0" />
                </td>
                <td>{{ item.ikeaTitle }}</td>
                <td>
                  <v-chip size="x-small" :color="typeColor(item.type)">
                    {{ item.type }}
                  </v-chip>
                </td>
                <td>{{ item.position || '-' }}</td>
                <td>{{ item.widthMm != null ? item.widthMm : '-' }}</td>
                <td>{{ item.heightMm != null ? item.heightMm : '-' }}</td>
                <td>{{ item.quantity }}</td>
                <td v-if="groupHasDoors(group)">
                  <v-autocomplete
                    v-if="item.type === 'door'"
                    v-model="item.hingeSide"
                    :items="hingeOptions"
                    density="compact"
                    hide-details
                    placeholder="Select"
                    class="mt-0 pt-0"
                    style="max-width: 80px"
                  />
                  <span v-else>-</span>
                </td>
                <td v-if="groupHasDoors(group)">
                  <v-autocomplete
                    v-if="item.type === 'door'"
                    v-model="item.hung"
                    :items="hungOptions"
                    density="compact"
                    hide-details
                    placeholder="Select"
                    class="mt-0 pt-0"
                    style="max-width: 80px"
                  />
                  <span v-else>-</span>
                </td>
                <td v-if="groupHasDoors(group)">
                  <v-autocomplete
                    v-if="item.type === 'door'"
                    v-model="item.special"
                    :items="specialOptions"
                    density="compact"
                    hide-details
                    placeholder="None"
                    class="mt-0 pt-0"
                    style="max-width: 110px"
                  />
                  <span v-else>-</span>
                </td>
              </tr>
            </tbody>
          </template>
        </v-table>
      </v-card>

      <div class="d-flex align-center mt-4">
        <span class="text-body-2 text-grey"> {{ selectedCount }} of {{ totalCount }} items selected </span>
        <v-spacer />
        <v-btn
          color="accent"
          variant="flat"
          :disabled="selectedCount === 0 || !allSelectedValid"
          @click="convertToFronts"
        >
          Convert
        </v-btn>
      </div>
    </div>

    <!-- Clear confirmation dialog -->
    <v-dialog v-model="showClearDialog" max-width="380">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">Clear scan?</v-card-title>
        <v-card-text>The current scan will be reset so that a new scan can be started.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showClearDialog = false">Cancel</v-btn>
          <v-btn
            color="red"
            variant="text"
            @click="
              () => {
                reset()
                showClearDialog = false
              }
            "
            >Confirm</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import uid from 'uid'
import { extractTextFromPdf, parseIkeaPlannerText, detectPdfRegion } from '@/js/ikeaPdfParser'
import productData from '@/js/productData'

export default {
  name: 'Scanner',
  props: ['quote', 'version', 'config'],
  data() {
    return {
      showClearDialog: false,
      dragging: false,
      parsing: false,
      converting: false,
      error: null,
      fileName: null,
      cabinetGroups: [],
      hingeOptions: [
        { text: 'BHD', value: 'bhd' },
        { text: 'LHD', value: 'lhd' },
        { text: 'RHD', value: 'rhd' },
        { text: 'THD', value: 'thd' }
      ],
      hungOptions: [
        { text: 'Base', value: 'b' },
        { text: 'Wall', value: 'w' }
      ],
      specialOptions: [
        { text: 'None', value: null },
        { text: 'Two Part', value: '2part' },
        { text: 'Corner', value: 'cnr' },
        { text: 'Appliance', value: 'a' },
        { text: 'Platsa', value: 'platsa' },
        { text: 'Pax', value: 'pax' },
        { text: 'Custom', value: 'custom' }
      ]
    }
  },
  computed: {
    materialHandleSet() {
      return this.version.materials.length > 0 && this.version.handles.length > 0
    },
    hasFrontsItems() {
      return this.version.fronts && this.version.fronts.length > 0
    },
    dropDisabled() {
      return !this.materialHandleSet || this.hasFrontsItems
    },
    selectedItems() {
      const items = []
      for (const group of this.cabinetGroups) {
        for (const item of group.items) {
          if (item.selected) items.push(item)
        }
      }
      return items
    },
    selectedCount() {
      return this.selectedItems.length
    },
    totalCount() {
      return this.cabinetGroups.reduce((sum, g) => sum + g.items.length, 0)
    },
    allSelectedValid() {
      return this.selectedItems.every((item) => {
        if (item.type === 'door') {
          return !!item.hingeSide && !!item.hung
        }
        return true
      })
    }
  },
  methods: {
    onFileDrop(event) {
      this.dragging = false
      if (this.dropDisabled) return
      const files = event.dataTransfer.files
      if (files.length > 0 && files[0].type === 'application/pdf') {
        this.processFile(files[0])
      } else {
        this.error = 'Please drop a valid PDF file.'
      }
    },
    onFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        this.processFile(file)
      }
      event.target.value = ''
    },
    async processFile(file) {
      this.parsing = true
      this.error = null
      this.fileName = file.name
      this.cabinetGroups = []

      try {
        const arrayBuffer = await file.arrayBuffer()
        const fullText = await extractTextFromPdf(arrayBuffer)

        // Region check: detect PDF region and compare to quote region
        const pdfRegion = detectPdfRegion(fullText)
        const quoteRegion = this.quote.region || 'uk'
        if (pdfRegion && pdfRegion !== quoteRegion) {
          const regionLabels = { uk: 'UK (£/cm)', us: 'US ($/inches)' }
          this.error = `This PDF appears to be ${regionLabels[pdfRegion] || pdfRegion} but the quote region is ${
            regionLabels[quoteRegion] || quoteRegion
          }. Please upload the correct regional IKEA planner.`
          this.parsing = false
          return
        }

        const groups = parseIkeaPlannerText(fullText)

        if (groups.length === 0) {
          this.error = 'No door or drawer front items found in this PDF. Make sure it is an IKEA planner export.'
        } else {
          // Assign unique IDs across all groups
          let id = 0
          for (const group of groups) {
            group.customItemNum = String(group.itemNum)
            for (const item of group.items) {
              item.id = id++
              item.hung = null
              item.special = null
            }
          }
          this.cabinetGroups = groups
        }
      } catch (err) {
        console.error('PDF parsing error:', err)
        this.error = 'Failed to parse PDF: ' + (err.message || 'Unknown error')
      } finally {
        this.parsing = false
      }
    },
    mapToFrontType(item) {
      if (item.type === 'int-drw') return 'drw-int'
      if (item.type === 'drawer front') return 'drw'
      if (item.type === 'door') {
        const hinge = item.hingeSide
        const hung = item.hung
        const special = item.special
        if (special) return hinge + '-' + hung + '-' + special
        return hinge + '-' + hung
      }
      return null
    },
    getIntDrwDimensions(item) {
      const region = this.quote.region || 'uk'
      const drwInt = productData.frontTypes.find((t) => t.value === 'drw-int')
      const presets = drwInt.presets[region] || drwInt.presets.uk

      // Map carcass width (from PDF) to preset front value by matching label
      const widthMm = item.widthMm
      const widthLabel = region === 'us' ? String(Math.round(widthMm / 25.4)) : String(widthMm)
      const widthPreset = presets.w.find((p) => p.label === widthLabel)
      const w = widthPreset ? widthPreset.value : widthMm

      // Map position (low/medium/high) to height preset value
      const posMap = { low: 'LO', medium: 'MED', high: 'HI' }
      const heightLabel = item.position ? posMap[item.position] : null
      const heightPreset = heightLabel ? presets.h.find((p) => p.label === heightLabel) : null
      const h = heightPreset ? heightPreset.value : undefined

      return { w, h }
    },
    getPresetOverrides(frontType) {
      const region = this.quote.region || 'uk'
      const ft = productData.frontTypes.find((t) => t.value === frontType)
      if (!ft || !ft.presets) return {}
      const presets = ft.presets[region] || ft.presets.uk
      if (!presets) return {}

      const overrides = {}
      // Only override if the preset is a single value (not a selection array)
      if (presets.w && presets.w.length === 1) {
        overrides.w = typeof presets.w[0] === 'object' ? presets.w[0].value : presets.w[0]
      }
      if (presets.h && presets.h.length === 1) {
        overrides.h = typeof presets.h[0] === 'object' ? presets.h[0].value : presets.h[0]
      }
      if (presets.qty && presets.qty.length === 1) {
        overrides.qty = presets.qty[0]
      }
      return overrides
    },
    convertToFronts() {
      this.converting = true
      setTimeout(() => {
        const defaultMaterial = this.version.materials[0].id
        const defaultHandle = this.version.handles[0].id

        for (const group of this.cabinetGroups) {
          for (const item of group.items) {
            if (!item.selected) continue

            let w = item.widthMm || undefined
            let h = item.heightMm || undefined
            let qty = item.quantity

            // Map int-drw to preset values
            if (item.type === 'int-drw') {
              const dims = this.getIntDrwDimensions(item)
              w = dims.w
              h = dims.h
            }

            // Apply single-option presets (e.g. corner qty=3, 2part w/h/qty)
            const frontType = this.mapToFrontType(item)
            const overrides = this.getPresetOverrides(frontType)
            if (overrides.w !== undefined) w = overrides.w
            if (overrides.h !== undefined) h = overrides.h
            if (overrides.qty !== undefined) qty = overrides.qty

            this.version.fronts.push({
              uid: uid(10),
              frontType: frontType,
              itemNum: group.customItemNum,
              w,
              h,
              qty,
              material: defaultMaterial,
              handle: defaultHandle
            })
          }
        }

        this.$emit('converted')
      }, 300)
    },
    typeColor(type) {
      if (type === 'door') return 'blue'
      if (type === 'drawer front') return 'green'
      if (type === 'int-drw') return 'orange'
      return 'grey'
    },
    groupHasDoors(group) {
      return group.items.some((i) => i.type === 'door')
    },
    reset() {
      this.cabinetGroups = []
      this.fileName = null
      this.error = null
      this.converting = false
    }
  }
}
</script>

<style scoped>
.scanner-container {
  max-width: 1200px;
  margin: 0 auto;
}

.drop-zone {
  border: 2px dashed #ccc !important;
  cursor: pointer;
  transition: all 0.2s ease;
}

.drop-zone--active {
  border-color: #ff847f !important;
  background-color: rgba(255, 132, 127, 0.05);
}

.drop-zone--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.drop-zone__content {
  padding: 60px 20px;
}

.clear-btn {
  transition: background-color 0.2s ease, color 0.2s ease;
}

.clear-btn:hover {
  background-color: rgba(244, 67, 54, 0.08);
  color: #f44336 !important;
}
</style>
